import React from 'react';

import copyTokens from '@amzn/meridian-tokens/base/icon/copy';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Row from '@amzn/meridian/row';

import './JsonDisplay.scss';

interface JsonDisplayProps {
    data: string;
    showCopyButton?: boolean;
}

const parseNestedJsonStrings = (obj: any): any => {
    if (typeof obj === 'string') {
        // Try to parse as JSON
        try {
            const parsed = JSON.parse(obj);
            // Recursively parse nested structures
            return parseNestedJsonStrings(parsed);
        } catch {
            // If it's not valid JSON, return as-is
            return obj;
        }
    } else if (Array.isArray(obj)) {
        // Process each array element
        return obj.map((item) => parseNestedJsonStrings(item));
    } else if (obj && typeof obj === 'object') {
        // Process each object property
        const result: any = {};
        for (const [key, value] of Object.entries(obj)) {
            result[key] = parseNestedJsonStrings(value);
        }
        return result;
    }

    return obj;
};

export const formatJsonString = (str: string): string => {
    try {
        const parsed = JSON.parse(str);

        // Parse nested JSON strings recursively
        const fullyParsed = parseNestedJsonStrings(parsed);

        // Stringify with proper formatting
        const formatted = JSON.stringify(fullyParsed, null, 2);

        // Handle escape sequences
        return formatted.replace(/\\n/g, '\n').replace(/\\t/g, '\t').replace(/\\r/g, '\r');
    } catch (error) {
        return str;
    }
};

export const JsonDisplay: React.FC<JsonDisplayProps> = ({ data, showCopyButton = true }) => {
    const copyToClipboard = async (text: string) => {
        try {
            await navigator.clipboard.writeText(text);
        } catch (err) {
            console.error('Failed to copy text: ', err);
        }
    };

    const formattedData = formatJsonString(data ?? '');

    return (
        <Column spacing="100" className="json-string-container">
            {showCopyButton && (
                <Row alignmentHorizontal="end">
                    <Button type="link" size="small" onClick={() => copyToClipboard(formattedData)}>
                        <Icon tokens={copyTokens} />
                    </Button>
                </Row>
            )}
            <pre className="json-string">{formattedData}</pre>
        </Column>
    );
};
